import { useRef, useState } from "react"
import { Box, IconButton, type SxProps, type Theme, Tooltip } from "@mui/material"
import ContentCopyIcon from "@mui/icons-material/ContentCopy"
import CheckIcon from "@mui/icons-material/Check"

type CopyOnHoverProps = {
  children: React.ReactNode
  /** Overrides the copied text when it differs from the visible content */
  value?: string
  /** Standard MUI Box sx overrides */
  sx?: SxProps<Theme>
}

/**
 * Wraps content with a copy-to-clipboard button that appears on hover.
 * The button copies `value` if provided, otherwise falls back to the element's text content.
 */
export const CopyOnHover = ({ children, value, sx }: CopyOnHoverProps) => {
  const [hovered, setHovered] = useState(false)
  const [copied, setCopied] = useState(false)
  const ref = useRef<HTMLSpanElement>(null)

  const handleCopy = () => {
    const text = value ?? ref.current?.textContent ?? ""
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        pointerEvents: "auto",
        ...sx,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span ref={ref}>{children}</span>
      <Tooltip title={copied ? "Copied!" : "Copy"}>
        <IconButton
          size="small"
          onClick={handleCopy}
          sx={{
            visibility: hovered ? "visible" : "hidden",
            padding: "2px",
          }}
        >
          {copied ? <CheckIcon fontSize="inherit" /> : <ContentCopyIcon fontSize="inherit" />}
        </IconButton>
      </Tooltip>
    </Box>
  )
}
